// import { Layers } from "@iko-design-center/shared";
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resizeImage } from "../../../services/screenshot.service"
import { useCavityWall } from "../../../store/cavity-wall/hooks"
import { useModal } from "../../../store/modal/hooks"
import { renderer } from "../../viewer/Renderer"
import { useApplication } from "../../../store/application/hooks"
import { isMobile } from "../../../services/helpers.service"
import { useAccountState } from "../../providers/AccountStateProvider"
import { CavityWallData } from "../../../store/cavity-wall/store"
import { ApplicationTypes } from "../../../store/application/store"
import { useQueryParams } from "../../../store/query/cavity-wall.hooks"
import { Models } from "../../viewer/renderer.api"
// import { checkValidQueriedValueOfType } from "../../../store/query/query-helpers"

type VisibleState = {
  wallType: boolean
  wallInsulation: boolean
  outerCavityLeaf: boolean
  flatRoof: boolean
  slopedRoof: boolean
  slopedRoofInsulation: boolean
  slopedRoofVaporShield: boolean
  slopedRoofConstruction: boolean
  details: boolean
}

export function useOverview() {
  const account = useAccountState()
  const { t, i18n } = useTranslation() as any
  const [, country] = i18n.language.split("_")
  const showExtendedThermalCalculation = country === "nl" && !isMobile()
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const {
    configurationState: { wallType, wallInsulation, outerCavityLeaf },
    thermalPerformance,
    setOverviewScreenshot,
    setHumanReadableName,
  } = useCavityWall()

  const wallTypeType = wallType.type!
  const wallInsulationType = wallInsulation.type!
  const outerCavityLeafType = outerCavityLeaf.type!
  const wallTypeMaterial = wallType.material!
  const wallInsulationMaterial = wallInsulation.material!
  const outerCavityLeafMaterial = outerCavityLeaf.material!

  const {
    displayCavityWallDownloadModal,
    displayCavityWallThermalCalculationModal,
    displayCavityWallExtendedThermalCalculationModal,
    displayRequestAdviceModal,
    displaySaveConfigurationModal,
    displaySaveConfigurationSuccessModal,
  } = useModal()
  const {
    currentModel,
    applicationType,
    directLink,
    setDirectLink,
  } = useApplication()
  const { setQueryParams } = useQueryParams()

  const initialVisibleState = useMemo(
    () => ({
      wallType: true,
      wallInsulation: true,
      outerCavityLeaf: true,
      flatRoof: true,
      slopedRoof: true,
      slopedRoofInsulation: true,
      slopedRoofVaporShield: true,
      slopedRoofConstruction: true,
      details: true,
    }),
    [],
  )
  const [visibleState, setVisibleState] = useState<VisibleState>(
    initialVisibleState,
  )
  const humanReadableName = `${wallType.type} / ${wallInsulation.type} / ${outerCavityLeaf.type}`

  useEffect(() => {
    setHumanReadableName(humanReadableName)
  }, [humanReadableName, setHumanReadableName])

  useEffect(() => {
    if (!directLink) {
      const data: CavityWallData = {
        wallType,
        wallInsulation,
        outerCavityLeaf,
        thermalPerformance,
      }
      setQueryParams(data, i18n.language)
    }

    // Handle direct link
    if (renderer && currentModel === Models.DOLLHOUSE && directLink) {
      setDirectLink(false)
      setTimeout(() => {
        let model = Models.CAVITY_WALL
        renderer.displayModel(model).then(() => {
          renderer.displayWallType(wallTypeType)
          renderer.toggleLayer("BINNENSPOUWBLAD", true)
          renderer.displayWallInsulation(wallInsulationType)
          renderer.toggleLayer("ISOLATIE", true)
          renderer.displayOuterCavityLeaf(outerCavityLeafType)
          renderer.toggleLayer("GEVELMETSELWERK", true)
        })
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    thermalPerformance,
    wallType,
    wallInsulation,
    outerCavityLeaf,
    setQueryParams,
    directLink,
  ])

  useEffect(
    () => {
      if (!isMobile()) renderer.displayMarkers()
      const dataURL = renderer.createScreenshot()
      resizeImage(dataURL, 520, 420).then((resizedDataURL) => {
        setOverviewScreenshot(resizedDataURL)
      })

      return () => {
        renderer.hideMarkers()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverviewScreenshot],
  )

  function toggleVisibleState(key: keyof VisibleState) {
    setVisibleState({ ...visibleState, [key]: !visibleState[key] })
  }

  useEffect(() => {
    setVisibleState(initialVisibleState)
    if (!isMobile()) renderer.displayMarkers()
    renderer.toggleAllLayers(applicationType, true)
    setTimeout(() => {
      renderer.toggleLayer("ISOLATIE-BEVESTIGING", true)
    }, 0)
  }, [currentModel, setVisibleState, initialVisibleState, applicationType])

  function handleWallTypeClick() {
    toggleVisibleState("wallType")
    renderer.toggleLayers("BINNENSPOUWBLAD")
  }
  function handleWallInsulationClick() {
    toggleVisibleState("wallInsulation")
    renderer.toggleLayers("ISOLATIE")
  }

  function handleOuterCavityLeafClick() {
    toggleVisibleState("outerCavityLeaf")
    renderer.toggleLayers("GEVELMETSELWERK")
  }

  function handleFlatRoofClick() {
    toggleVisibleState("flatRoof")
    renderer.toggleLayers("PLAT_DAK")
  }

  function handleSlopedRoofClick(toggle = "slopedRoof", layer = "HELLEND_DAK") {
    toggleVisibleState(toggle as keyof VisibleState)
    renderer.toggleLayers(layer)
  }

  const copyShareLink = () => {
    const link = window.location.href
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
    } else {
      window.prompt(t("flatRoof.overview.shareConfiguration"), link)
      setLinkCopied(true)
    }
  }

  function handleAdjustSpecificationsClick() {
    if (showExtendedThermalCalculation) {
      displayCavityWallExtendedThermalCalculationModal()
    } else {
      displayCavityWallThermalCalculationModal()
    }
  }

  function handleDownloadFilesClick() {
    displayCavityWallDownloadModal()
  }

  function handleRequestAdviceClick() {
    displayRequestAdviceModal()
  }

  function handleDetailsClick() {
    toggleVisibleState("details")
    renderer.toggleDetails()
  }

  function handleSaveConfigurationClick() {
    if (!account.isAuthenticated) return

    displaySaveConfigurationModal(async (name: string) => {
      const cavityWallData: CavityWallData = {
        wallType: wallType,
        wallInsulation: wallInsulation,
        outerCavityLeaf: outerCavityLeaf,
        thermalPerformance: thermalPerformance,
      }
      const cavityWallConfiguration = {
        application_type: ApplicationTypes.CAVITY_WALL,
        application_data: cavityWallData,
        display_name: name ?? `${t("home.cavityWall")}`,
        language: i18n.language,
      }
      account
        .saveConfiguration(cavityWallConfiguration)
        .then((response) => {
          displaySaveConfigurationSuccessModal(
            response?.saved_configuration?.record_id
          )
        })
        .catch(() => {
          console.error("Failed to save configuration")
        })
    })
  }

  return {
    t,
    linkCopied,
    currentModel,
    thermalPerformance,
    visibleState,
    wallTypeType,
    wallTypeMaterial,
    wallInsulationType,
    wallInsulationMaterial,
    outerCavityLeafType,
    outerCavityLeafMaterial,
    displayCavityWallDownloadModal,
    displayRequestAdviceModal,
    displayCavityWallThermalCalculationModal,
    handleWallTypeClick,
    handleWallInsulationClick,
    handleOuterCavityLeafClick,
    handleFlatRoofClick,
    handleSlopedRoofClick,
    handleDetailsClick,
    handleAdjustSpecificationsClick,
    handleDownloadFilesClick,
    handleRequestAdviceClick,
    copyShareLink,
    handleSaveConfigurationClick,
  }
}
